import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react'
import { PersistentToast } from '.'
import { message as messageApi } from 'antd'
import { Router, useLocation } from 'react-router-dom'
import { useCustomHistory } from 'hooks/useHistory'
import { TextVariant } from 'blaze-ui/Text/Text'

interface PersistentToastMessageProps {
  icon?: ReactNode
  title?: ReactNode
  showCloseButton?: boolean
  message: string | ReactNode
  onClose?: () => void
  closeOnNavigationChange?: boolean
  titleVariant?: TextVariant
  messageVariant?: TextVariant
  checkbox?: {
    label: string
    checked: boolean
    onChange: (checked: boolean) => void
  }
  primaryAction?: {
    label: string
    onPress: () => void
  }
  secondaryAction?: {
    label: string
    onPress: () => void
  }
  actionOrder?: 'secondaryFirst' | 'primaryFirst'
}

export const PersistentToastMessage = ({
  icon,
  message,
  title,
  showCloseButton,
  checkbox,
  primaryAction,
  secondaryAction,
  closeOnNavigationChange = false,
  titleVariant,
  messageVariant = 'secondaryDark',
  actionOrder = 'secondaryFirst',
  onClose = () => messageApi.destroy(),
}: PersistentToastMessageProps) => {
  const location = useLocation()
  const prevPathRef = useRef(location.pathname)

  useEffect(() => {
    if (!closeOnNavigationChange) {
      return
    }

    const currentPath = location.pathname

    if (prevPathRef.current !== currentPath) {
      onClose()
      prevPathRef.current = currentPath
    }
  }, [location, onClose, closeOnNavigationChange])

  return (
    <PersistentToast.Root>
      <PersistentToast.Header
        onClose={onClose}
        icon={icon}
        showCloseButton={showCloseButton}
        titleVariant={titleVariant}
      >
        {title}
      </PersistentToast.Header>
      {checkbox && (
        <PersistentToast.Checkbox label={checkbox.label} checked={checkbox.checked} onChange={checkbox.onChange} />
      )}
      {message ? <PersistentToast.Message variant={messageVariant}>{message}</PersistentToast.Message> : null}
      {(primaryAction || secondaryAction) && (
        <PersistentToast.Actions>
          {actionOrder === 'secondaryFirst' ? (
            <>
              <SecondaryAction secondaryAction={secondaryAction} />
              <PrimaryAction primaryAction={primaryAction} />
            </>
          ) : (
            <>
              <PrimaryAction primaryAction={primaryAction} />
              <SecondaryAction secondaryAction={secondaryAction} />
            </>
          )}
        </PersistentToast.Actions>
      )}
    </PersistentToast.Root>
  )
}

const PrimaryAction = ({ primaryAction }: { primaryAction: PersistentToastMessageProps['primaryAction'] }) => {
  if (!primaryAction) return null

  return (
    <PersistentToast.PrimaryAction onPress={primaryAction.onPress}>{primaryAction.label}</PersistentToast.PrimaryAction>
  )
}

const SecondaryAction = ({ secondaryAction }: { secondaryAction: PersistentToastMessageProps['secondaryAction'] }) => {
  if (!secondaryAction) return null

  return (
    <PersistentToast.SecondaryAction onPress={secondaryAction.onPress}>
      {secondaryAction.label}
    </PersistentToast.SecondaryAction>
  )
}

// This Router isntance is used solely to give access to `useLocation` and history
const PersistentToastMessageWrapper = ({ children }: PropsWithChildren) => {
  return <Router history={useCustomHistory()}>{children}</Router>
}

export const showPersistentToastMessage = (props: PersistentToastMessageProps) => {
  const Message = (
    <PersistentToastMessageWrapper>
      <PersistentToastMessage {...props} />
    </PersistentToastMessageWrapper>
  )

  messageApi.success({
    content: Message,
    duration: 0,
    type: 'success',
    key: 'persistent-toast',
  })
}
